<template>
  <v-dialog v-model="copyDialog" max-width="1000px" scrollable>
    <v-card class="elevation-1 copy-rule vop-bg-whisper">
      <v-layout column justify-space-between>
        <v-layout column>
          <!-- Toolbar -->
          <v-toolbar flat class="vop-bg-whisper">
            <!-- Title -->
            <v-toolbar-title class="subheading ttu font-weight-medium">
              <span v-if="ruleType === 'rule'"> {{ messages.label.COPY_RULE }}</span>
              <span v-if="ruleType === 'ruleSet'"> {{ messages.label.COPY_RULE_SET }}</span>
              <span v-if="ruleType === 'ruleCollection'"> {{ messages.label.COPY_RULE_COLLECTION }}</span>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <!-- Close button -->
            <v-toolbar-items>
              <v-btn icon medium @click="hide()">
                <v-icon>mdi-window-close</v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <!-- Main Content -->
          <v-card-text class="pa-2">
            <v-layout row wrap>
              <!-- Error Alert -->
              <v-flex xs12 v-if="errorAlert">
                <v-alert v-model="errorAlert" dismissible type="error" class="mt-0">
                  {{ errorMessage }}
                </v-alert>
              </v-flex>
              <!-- Original Rule Info -->
              <v-flex xs5>
                <h2 class="body-2 ttu caption pa-2 ml-2 font-weight-bold">{{ messages.label.ORIGINAL }}</h2>
                <v-data-table :items="ruleDetails" hide-actions hide-headers class="pa-2">
                  <template slot="items" slot-scope="props">
                    <tr>
                      <td class="text-xs-left ttu">{{ props.item.name | ownerLabel }}</td>
                      <td class="text-xs-left">
                        <!-- Owner -->
                        <template v-if="props.item.name === 'ownerId'">
                          {{ getOwnerName(props.item.value) }}
                        </template>
                        <!-- Provider -->
                        <template v-else-if="props.item.name === 'secondaryOwner'">
                          {{ getProviderName(props.item.value) }}
                        </template>
                        <template v-else>
                          <div>
                            {{ props.item.value | displayNA }}
                          </div>
                        </template>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-flex>
              <!-- Copy Rule Info -->
              <v-flex xs6 offset-xs1>
                <h2 class="body-2 ttu caption pa-2 font-weight-bold">{{ messages.label.NEW }}</h2>
                <v-form class="px-2">
                  <!-- Rule Title -->
                  <v-flex xs10>
                    <v-text-field v-model="copyRuleData.title" clearable v-validate="'required|max:100'"
                      :error-messages="errors.collect('title')" data-vv-name="title" :counter="100"
                      :label="messages.label.RULE_TITLE" required>
                    </v-text-field>
                  </v-flex>
                  <!-- Rule Description -->
                  <v-flex xs10>
                    <v-textarea v-model="copyRuleData.description" clearable v-validate="'max:100'"
                      :error-messages="errors.collect('description')" data-vv-name="description" :counter="500"
                      :label="messages.label.RULE_DESCRIPTION" required>
                    </v-textarea>
                  </v-flex>
                  <!-- Provider Group -->
                  <div v-if="IS_POLARIS">
                    <v-flex sm6>
                      <v-autocomplete v-model="selectedProviderGroup" :label="messages.label.PROVIDER_GROUP"
                        item-text="title" :loading="loadingProviderGroups" item-value="title"
                        :items="providerGroupsList" return-object
                        @change="retrieveSecondaryOwners(selectedProviderGroup)">
                        <template v-slot:no-data>
                          <v-list-tile>
                            <v-list-tile-title>
                              No Provider groups
                            </v-list-tile-title>
                          </v-list-tile>
                        </template>
                      </v-autocomplete>
                    </v-flex>
                    <!-- Provider -->
                    <v-flex xs6>
                      <v-autocomplete v-model="selectedSecondaryOwner" :label="messages.label.PROVIDER_NAME"
                        :loading="loadingSecondaryOwner" item-text="title" item-value="title" return-object
                        :items="secondaryOwners">
                      </v-autocomplete>
                    </v-flex>
                  </div>
                  <div v-else>
                    <!-- Sub Account -->
                    <v-flex sm6 v-if="mpxSubAccounts.length > 0">
                      <v-autocomplete v-model="selectedSubAccount" :label="messages.label.SUB_ACCOUNT" item-text="title"
                        item-value="title" :loading="loadingSubAccounts" :items="mpxSubAccounts" return-object>
                      </v-autocomplete>
                    </v-flex>
                    <!-- Sub Account Title-->
                    <v-flex sm6 v-else>
                      <v-text-field v-model="providerName" v-validate="'required|max:100'" data-vv-name="providerName"
                        :error-messages="errors.collect('providerName')" :counter="100"
                        :label="messages.label.PROVIDER_NAME" required>
                      </v-text-field>
                    </v-flex>
                  </div>
                </v-form>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-layout>
        <v-card-actions class="px-3">
          <v-spacer></v-spacer>
          <v-btn color="grey lighten-1 white--text" @click="hide()">
            {{ messages.label.CANCEL }}
          </v-btn>
          <v-btn color="primary" :disabled="copyInProgress" :loading="copyInProgress" @click="copy()">
            {{ messages.label.COPY }}
          </v-btn>
        </v-card-actions>
      </v-layout>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex"
import { GET_PROVIDER_GROUPS, GET_MPX_SUB_ACCOUNTS } from "@/store/constants/action-types"
import {
  IS_POLARIS,
  RULE_MGMT_DATA_SERVICE_URL,
  VOD_SYCN_PREINGEST_DATA_SERVICE_URL
} from "@/store/constants/getter-types"
import { SECONDARY_OWNER_ANY } from "@/Shared/constants"
import * as metadataRulesDataService from "@/MetadataRules/MetadataRulesDataService"
import MetadataRulesService from "@/MetadataRules/MetadataRulesService"
import AppService from "@/AppService"
import * as utils from "@/Shared/utils"
import messages from "@/MetadataRules/messages.json"

/**
 * initialData
 */
function initialData(instance) {
  return {
    ruleType: "rule",
    copyInProgress: false,
    errorAlert: false,
    errorMessage: "",
    ruleDetails: [],
    copyRuleData: {},
    loadingProviderGroups: false,
    selectedProviderGroup: {},
    selectedSubAccount: {},
    providerName: "",
    loadingSubAccounts: false,
    loadingSecondaryOwner: false,
    secondaryOwners: [],
    selectedSecondaryOwner: {}, // providers are secondary owners
    dictionary: instance.getValidationDictionary()
  }
}

export default {
  name: "MetadataRuleCopy",
  $_veeValidate: {
    validator: "new"
  },
  props: {
    value: Boolean,
    ruleObj: Object
  },
  data: function () {
    return initialData(this)
  },
  watch: {
    copyDialog(newValue) {
      if (newValue === true) {
        this.show()
      }
    }
  },
  computed: {
    ...mapState([
      "mpxParentAccount", "providerGroups", "mpxSubAccounts"
    ]),
    ...mapGetters([
      RULE_MGMT_DATA_SERVICE_URL, IS_POLARIS, VOD_SYCN_PREINGEST_DATA_SERVICE_URL
    ]),
    copyDialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit("input", value)
      }
    },
    providerGroupsList() {
      let list = this.$lodash.cloneDeep(this.providerGroups)
      list.unshift({ title: "Any" })
      return list
    }
  },
  methods: {
    ...mapActions([
      GET_PROVIDER_GROUPS, GET_MPX_SUB_ACCOUNTS
    ]),

    /**
     * reset
     */
    reset() {
      Object.assign(this.$data, initialData(this))
    },

    /**
     * getValidationDictionary - Validation messages for vee-validate
     * @returns {object}
     */
    getValidationDictionary() {
      return {
        custom: {
          title: {
            required: () => "Title can't be empty",
            max: "Title may not be greater than 100 characters"
          },
          description: {
            max: "Description may not be greater than 500 characters"
          },
          providerName: {
            required: () => "Provider Name can't be empty",
            max: "Provider Name may not be greater than 100 characters"
          }
        }
      }
    },

    /**
     * hide - hide copy modal
     */
    hide() {
      this.resetCopyData()
      this.copyDialog = false
    },

    /**
     * show - Show Copy Rule / Rule Set / Rule Collection Modal
     */
    show() {
      this.resetCopyData()
      this.initializeValue()
      this.parseRuleType()
      this.constructRuleDetails()
      if (this.IS_POLARIS) {
        this.retrieveProviderGroups()
      } else {
        this.retrieveSubAccounts()
      }
    },

    /**
     * Re-initialize
     */
    resetCopyData() {
      this.$validator.reset()
      this.ruleType = "rule"
      this.copyInProgress = false
      this.errorAlert = false
      this.errorMessage = ""
      this.copyRuleData = {}
    },

    /**
     * initialize value for copyRuleData
     */
    initializeValue() {
      this.copyRuleData = this.$lodash.cloneDeep(this.ruleObj)
      this.copyRuleData.title = "COPY - " + this.ruleObj.title
      this.copyRuleData.description = "COPY - " + this.ruleObj.description
    },

    /**
     * retrieveProviderGroups - retrieve provider groups
     */
    retrieveProviderGroups() {
      this.loadingProviderGroups = true
      this.GET_PROVIDER_GROUPS()
        .then(() => {
          this.selectedProviderGroup = this.providerGroupsList[0]
          return this.retrieveSecondaryOwners(this.selectedProviderGroup)
        })
        .catch(() => {
          // TODO: handle error
        }).finally(() => {
          this.loadingProviderGroups = false
        })
    },

    /**
     * retrieveSubAccounts - retrieve provider groups
     */
    retrieveSubAccounts() {
      this.loadingSubAccounts = true
      this.GET_MPX_SUB_ACCOUNTS()
        .then(() => {
          let currentOwnerId = this.ruleObj.ownerId
          if (!this.$lodash.isUndefined(currentOwnerId)) {
            this.selectedSubAccount = this.$lodash.find(this.mpxSubAccounts, { "id": currentOwnerId })
          } else {
            this.selectedSubAccount = this.mpxSubAccounts[0]
          }
        })
        .catch(() => {
          // TODO: handle error
        }).finally(() => {
          this.loadingSubAccounts = false
        })
    },

    /**
     * retrieveSecondaryOwners - Retrieve all providers from API service
     * @param {string} selectedSecondaryOwners
     */
    retrieveSecondaryOwners(providerGroup) {
      let queryObject = {}
      if (providerGroup.title === "Any") {
        return new Promise((resolve) => {
          this.secondaryOwners = []
          this.secondaryOwners.push({ title: "Any" })
          this.selectedSecondaryOwner = this.secondaryOwners[0]
          resolve()
        })
      } else {
        this.loadingSecondaryOwner = true
        queryObject["byProviderGroupId"] = providerGroup.id
        return metadataRulesDataService
          .retrieveSecondaryOwners(queryObject, this.VOD_SYCN_PREINGEST_DATA_SERVICE_URL)
          .then((secondaryOwners) => {
            this.secondaryOwners = secondaryOwners
            this.selectedSecondaryOwner = this.secondaryOwners[0]
          })
          .catch(() => {
            // TODO: handle error
          }).finally(() => {
            this.loadingSecondaryOwner = false
          })
      }
    },

    /**
     * parseRuleType
     */
    parseRuleType() {
      if (!this.$lodash.isUndefined(this.ruleObj)) {
        let isRuleCollection = this.ruleObj.id.indexOf("/RuleCollection/") > 0
        let isRuleSet = this.ruleObj.id.indexOf("/RuleSet/") > 0
        let isRule = this.ruleObj.id.indexOf("/Rule/") > 0
        if (isRuleCollection) {
          this.ruleType = "ruleCollection"
        } else if (isRuleSet) {
          this.ruleType = "ruleSet"
        } else if (isRule) {
          this.ruleType = "rule"
        }
      }
    },

    /**
     * constructRuleDetails - Construct selected rule details
     * This is being done to display in a data table
     */
    constructRuleDetails() {
      this.ruleDetails = []
      // Convert object in to array of objects with key value pairs with required fields
      let requiredFields = ["title", "description", "ownerId", "providerGroup", "secondaryOwner"]
      requiredFields.forEach((fieldName) => {
        let obj = {}
        if (this.ruleObj.hasOwnProperty(fieldName)) {
          obj["name"] = fieldName
          obj["value"] = this.ruleObj[fieldName]
          this.ruleDetails.push(obj)
        } else if (fieldName === "providerGroup") {
          if (this.IS_POLARIS) {
            obj["name"] = fieldName
          } else {
            obj["name"] = "Sub Account"
          }
          obj["value"] = this.getProviderGroupName(this.ruleObj.ownerId)
          this.ruleDetails.push(obj)
        }
      })
    },

    /**
     * getOwnerName
     * @param {stirng} ownerId
     */
    getOwnerName(ownerId) {
      if (this.IS_POLARIS) {
        return MetadataRulesService.getOwnerName(ownerId, this.mpxParentAccount.id, this.providerGroups, true)
      } else {
        return MetadataRulesService.getOwnerName(ownerId, this.mpxParentAccount.id, this.mpxSubAccounts, false)
      }
    },

    /**
     * getProviderGroupName
     * @param {stirng} ownerId
     */
    getProviderGroupName(ownerId) {
      if (this.IS_POLARIS) {
        return MetadataRulesService.getProviderGroupName(ownerId, this.mpxParentAccount.id, this.providerGroups, true)
      } else {
        return MetadataRulesService.getProviderGroupName(ownerId, this.mpxParentAccount.id, this.mpxSubAccounts, false)
      }
    },

    /**
     * getProviderName
     * @param {stirng} secondaryOwner
     */
    getProviderName(secondaryOwner) {
      return MetadataRulesService.getProviderName(secondaryOwner)
    },

    /**
     * copy - copy rule / rule set / rule collection
     */
    copy() {
      this.$validator.validateAll()
        .then((result) => {
          if (result) {
            this.copyInProgress = true
            let ownerId = ""
            if (this.IS_POLARIS) {
              if (this.selectedProviderGroup.title === "Any") {
                ownerId = this.mpxParentAccount.id
              } else {
                ownerId = this.selectedProviderGroup.ownerId
              }
              if (this.selectedSecondaryOwner.title === "Any") {
                this.copyRuleData.secondaryOwner = SECONDARY_OWNER_ANY
              } else {
                this.copyRuleData.secondaryOwner = this.selectedSecondaryOwner.title
              }
            } else {
              if (this.$lodash.isEmpty(this.selectedSubAccount)) {
                ownerId = this.mpxParentAccount.id
              } else {
                ownerId = this.selectedSubAccount.id
              }
              if (this.$lodash.isEmpty(this.providerName.trim())) {
                this.copyRuleData.secondaryOwner = SECONDARY_OWNER_ANY
              } else {
                this.copyRuleData.secondaryOwner = this.providerName
              }
            }

            let omitProperties = ["id", "guid", "updated", "added", "ownerId", "addedByUserId", "updatedByUserId", "version", "_added", "_enabled", "_updated"]
            this.copyRuleData = this.$lodash.omit(this.copyRuleData, omitProperties)
            let copyRulePromise = {}
            switch (this.ruleType) {
              case "rule":
                copyRulePromise = metadataRulesDataService.createRule(this.copyRuleData, ownerId, this.RULE_MGMT_DATA_SERVICE_URL)
                break
              case "ruleSet":
                copyRulePromise = metadataRulesDataService.createRuleSet(this.copyRuleData, ownerId, this.RULE_MGMT_DATA_SERVICE_URL)
                break
              case "ruleCollection":
                copyRulePromise = metadataRulesDataService.createRuleCollection(this.copyRuleData, ownerId, this.RULE_MGMT_DATA_SERVICE_URL)
                break
            }
            copyRulePromise.then(() => {
              MetadataRulesService.$emit("event:showCopyRuleSuccessSnackBar", this.ruleType)
              this.hide()
            }).catch((error) => {
              this.showError(error)
            }).finally(() => {
              this.copyInProgress = false
            })
          }
        })
    },

    /**
     * showError - Handles displaying error
     * @param {object} error
     */
    showError(error) {
      let isServerError = utils.isMPXError(error)
      let isClientError = utils.isClientError(error)
      if (isServerError) {
        this.errorAlert = true
        this.errorMessage = error.response.data.description
      } else if (isClientError) {
        this.errorAlert = true
        this.errorMessage = error.message
      }
    }
  },
  filters: {
    ownerLabel(value) {
      return MetadataRulesService.getOwnerLabel(value)
    },
    displayNA(value) {
      if (value === null || value === "") {
        return "N/A"
      } else {
        return value
      }
    }
  },
  mounted() {
    // Validator initialization
    this.$validator.localize("en", this.dictionary)
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Events
     */
    AppService.$on("event:mpxAccountChanged", this.reset)
  },
  beforeDestroy() {
    AppService.$off("event:mpxAccountChanged", this.reset)
  }
}
</script>

<style lang="scss">
.copy-rule {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>



// WEBPACK FOOTER //
// src/MetadataRules/MetadataRuleCopy.vue